import { useCallback } from "react";
import { Visit } from "@visitproof/types";
import { View } from "react-big-calendar";
import { Timestamp } from "firebase/firestore";
import { SchedulerActionsProps } from "../types";

export const useSchedulerActions = ({
  user,
  account,
  openDialog,
  navigate,
  setDate,
  setRange,
  setUpdatingEvent,
  calendarView,
  setCalendarView,
}: SchedulerActionsProps) => {
  
  const handleSelectSlot = useCallback((data: any) => {
    if (calendarView === "month") {
      setDate(data.start);
      setCalendarView("day");
      return;
    }

    if (!account?.id) return;

    openDialog({
      visit: {
        startDate: Timestamp.fromDate(data.start),
        endDate: Timestamp.fromDate(data.end),
        accountId: account.id,
        createdBy: user?.uid || "",
      },
      onVisitCreated: (visitId: string) => {
        navigate(`/visit/${visitId}`);
      },
    });
  }, [calendarView, setDate, setCalendarView, openDialog, account?.id, user?.uid, navigate]);

  const handleRangeChange = useCallback((range: any) => {
    let start: Date, end: Date;

    if (Array.isArray(range)) {
      if (range.length === 1 && range[0] instanceof Date) {
        // Day view: single Date object
        start = new Date(range[0]);
        end = new Date(range[0]);
        end.setDate(end.getDate() + 1);
      } else if (typeof range[0] === "string") {
        // Week/month view: array of ISO strings
        const parsed = range.map((d: string) => new Date(d));
        parsed.sort((a: Date, b: Date) => a.getTime() - b.getTime());
        start = parsed[0];
        end = parsed[parsed.length - 1];
        end.setDate(end.getDate() + 1);
      } else if (range[0] instanceof Date) {
        // Week/month view: array of Dates
        const sorted = [...range].sort((a: Date, b: Date) => a.getTime() - b.getTime());
        start = sorted[0];
        end = sorted[sorted.length - 1];
        end.setDate(end.getDate() + 1);
      } else {
        console.warn("Unhandled range array format:", range);
        return;
      }
    } else if (range?.start && range?.end) {
      // Agenda or object-based range
      start = new Date(range.start);
      end = new Date(range.end);
    } else {
      console.warn("Unknown range format:", range);
      return;
    }

    // Normalize to avoid timezone discrepancies
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    // Only update the range for data fetching - do NOT update date here.
    // The date is managed by onNavigate and must not be overwritten by range changes.
    // The range start can be different from the navigated date (e.g., month view shows
    // days from previous/next month in the grid).
    setRange({ start, end });
  }, [setRange]);

  const handleEventChanged = useCallback((data: any) => {
    const visit = data.event.resource as Visit;
    const newVisit = { 
      ...visit, 
      startDate: Timestamp.fromDate(data.start), 
      endDate: Timestamp.fromDate(data.end) 
    };
    setUpdatingEvent(newVisit);
  }, [setUpdatingEvent]);

  const handleNylasButtonClick = useCallback(() => {
    if (!user) return;

    const client_id = account?.nylasClientId ?? process.env.REACT_APP_NYLAS_CLIENT_ID;
    const redirect_uri = window.location.origin + "/callback/nylas";
    const response_type = "code";
    const prompt = "detect";
    const url = `https://api.us.nylas.com/v3/connect/auth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&prompt=${prompt}&login_hint=${user.email}`;
    window.open(url, "_blank");
  }, [user, account?.nylasClientId]);

  const handleDeleteEvent = useCallback(async (event: any) => {
    const visit = event.resource as Visit;
    if (visit.status !== "pending") return;
    
    try {
      // This would need to be implemented with proper service
      console.log("Delete event:", visit.id);
    } catch (err) {
      console.error("Failed to delete visit:", err);
    }
  }, []);

  const handleNavigate = useCallback((newDate: Date) => {
    setDate(newDate);
  }, [setDate]);

  const handleViewChange = useCallback((view: View) => {
    setCalendarView(view);
  }, [setCalendarView]);

  const handleDoubleClickEvent = useCallback((event: any) => {
    const visit = event.resource as Visit;
    navigate(`/visit/${visit.id}`);
  }, [navigate]);

  const handleCloseUpdateAlert = useCallback(() => {
    setUpdatingEvent(null);
  }, [setUpdatingEvent]);

  return {
    handleSelectSlot,
    handleRangeChange,
    handleEventChanged,
    handleNylasButtonClick,
    handleDeleteEvent,
    handleNavigate,
    handleViewChange,
    handleDoubleClickEvent,
    handleCloseUpdateAlert,
  };
}; 